import React, { useState, useEffect, useCallback } from 'react';
import SearchableSelect from './SearchableSelect';
import api from '../../services/api';

// Shared vendor cache to prevent duplicate API calls
let vendorCache: string[] | null = null;
let vendorPromise: Promise<string[]> | null = null;

const loadVendorsOnce = async (): Promise<string[]> => {
  if (vendorCache) {
    return vendorCache;
  }
  
  if (vendorPromise) {
    return vendorPromise;
  }
  
  vendorPromise = (async () => {
    try {
      const response = await api.get('/vendors');
      if (response.data.success) {
        const vendorNames = response.data.data.map((vendor: any) => vendor.name);
        vendorCache = vendorNames;
        return vendorNames;
      }
      return [];
    } catch (error) {
      console.error('Error loading vendors:', error);
      return [];
    } finally {
      vendorPromise = null;
    }
  })();
  
  return vendorPromise;
};

interface VendorDropdownProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
}

const VendorDropdown: React.FC<VendorDropdownProps> = ({ value, onChange, placeholder = "Select vendor", required = false }) => {
  const [vendors, setVendors] = useState<string[]>(vendorCache || []);
  const [loading, setLoading] = useState(false);
  const [isNewVendor, setIsNewVendor] = useState(false);

  useEffect(() => {
    if (!vendorCache) {
      setLoading(true);
      loadVendorsOnce().then(vendorNames => {
        setVendors(vendorNames);
        setLoading(false);
      });
    }
  }, []);

  const vendorOptions = vendors.map(vendor => ({ value: vendor, label: vendor }));

  if (isNewVendor) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isNewVendor}
            onChange={(e) => setIsNewVendor(e.target.checked)}
            className="rounded"
          />
          <label className="text-sm text-gray-600">Add new vendor</label>
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter new vendor name"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          required={required}
        />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={isNewVendor}
          onChange={(e) => setIsNewVendor(e.target.checked)}
          className="rounded"
        />
        <label className="text-sm text-gray-600">Add new vendor</label>
      </div>
      <SearchableSelect
        options={vendorOptions}
        value={value}
        onChange={(selectedValue) => onChange(selectedValue.toString())}
        placeholder={placeholder}
        disabled={loading}
        className="text-sm"
      />
    </div>
  );
};

export default React.memo(VendorDropdown);